import { navigate } from "@reach/router";
import { Mapping } from "pages/dashboard/admin/import/interest_imports/types";
import { Problem } from "pages/dashboard/admin/import/interest_imports/validate";
import { StepData } from "..";

export type ValidatorType = (data: StepData) => boolean;

export type ValidationRule = {
  condition: (data: StepData) => boolean;
  redirectTo: string;
};

// Step-specific validators
export const uploadValidator: ValidatorType = (data) => !!data.file;
export const organizeValidator: ValidatorType = (data) => !!data.listId && data.timezone && data.timezone !== "none";
export const matchValidator: ValidatorType = (data) => Object.keys(data.mapping).length > 2;
export const errorsValidator: ValidatorType = (data) => data.problems.filter(({ type }) => type !== "default").length === 0;

// Shared validation rules for step navigation
const sharedValidations: Record<string, ValidationRule> = {
  file: {
    condition: (data: StepData): data is StepData & { file: File } => !uploadValidator(data),
    redirectTo: "./upload",
  },
  listId: {
    condition: (data: StepData): data is StepData & { listId: string } => !organizeValidator(data),
    redirectTo: "./organize",
  },
  mapping: {
    condition: (data: StepData): data is StepData & { mapping: Mapping } => !matchValidator(data),
    redirectTo: "./match",
  },
  problems: {
    condition: (data: StepData): data is StepData & { problems: Problem[] } => !errorsValidator(data),
    redirectTo: "./errors",
  },
};

const stepValidations: Record<string, ValidationRule[]> = {
  organize: [sharedValidations.file],
  match: [sharedValidations.file, sharedValidations.listId],
  validate: [sharedValidations.file, sharedValidations.listId, sharedValidations.mapping],
  errors: [sharedValidations.file, sharedValidations.listId, sharedValidations.mapping],
  finalize: [sharedValidations.file, sharedValidations.listId, sharedValidations.mapping, sharedValidations.problems],
};

export const validateStep = (
  stepData: StepData,
  step: keyof typeof stepValidations,
): stepData is StepData & {
  file: File;
  listId: string;
  mapping: Mapping;
  problems: Problem[];
} => {
  const rules = stepValidations[step];
  for (const rule of rules) {
    if (rule.condition(stepData)) {
      navigate(rule.redirectTo, { replace: true });
      return false;
    }
  }
  return true;
};
